<template>
  <div class="app-shell">
    <EventGate v-if="gated" :status="eventStatus" :start-at="eventStartAt" />
    <template v-else>
      <main class="app-main" :class="{ 'no-nav': hideNav }">
        <router-view />
      </main>
      <BottomNav v-if="!hideNav" />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import BottomNav from "@/components/BottomNav.vue";
import EventGate from "@/components/EventGate.vue";
import { useEventTitle } from "@/composables/useEventTitle";
import { useEventConfig } from "@/composables/useEventConfig";
import { useBrand } from "@/composables/useBrand";

const route = useRoute();
const auth = useAuthStore();

const { loadEventTitle } = useEventTitle();
const { loadEventConfig, isLive, eventStatus, eventStartAt } = useEventConfig();
const { loadBrand } = useBrand();

// 綁定場未上線(draft/ended/archived)→ 全屏攔截取代正常首頁。
// DEFAULTS.is_live=true 確保 /event-config 回來前不閃攔截。
// 選擇活動頁(尚未綁定活動)永不顯示攔截頁。
const gated = computed(() => route.name !== "select-event" && isLive.value === false);
onMounted(() => {
  loadEventTitle();
  loadEventConfig();
  loadBrand();
});

/** Routes that always hide the bottom nav (forced-fill forms + bar QR landing).
 *  Home is special-cased below so the login splash takes the full viewport
 *  before the user has a session. */
const HIDE_NAV = new Set([
  "consent",
  "me-profile",
  "checkin",
  "select-event",
]);

const hideNav = computed(() => {
  if (HIDE_NAV.has(route.name as string)) return true;
  if (route.name === "home" && !auth.isLoggedIn) return true;
  return false;
});
</script>

<style>
.app-shell {
  min-height: 100vh;
  background: var(--bg-black);
  color: var(--text-primary);
}
.app-main {
  /* Reserve space for the fixed bottom nav (~60px + safe-area). */
  padding-bottom: calc(72px + env(safe-area-inset-bottom));
  min-height: 100vh;
  box-sizing: border-box;
}
.app-main.no-nav {
  padding-bottom: 0;
}
</style>
